import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseDelete from '@/assets/images/closeDelete.svg?react';
import { BUTTON_SOURCES } from '@/constants/buttonSources';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useDragToDismiss } from '@/hooks/useDragToDismiss';
import { useLanguage } from '@/hooks/useLanguage';
import { useScrollLock } from '@/hooks/useScrollLock';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import { trackButtonClick } from '@/utils/gtmButtonClicked';
import styles from './BottomSheet.module.scss';

interface Props {
  /**
   * Whether the sheet is closable.
   * If true, a close button will be rendered.
   */
  closable: boolean;
  /**
   * Callback triggered after the sheet close animation completes.
   */
  onClose?: () => void;
  /**
   * Content to display inside the sheet.
   */
  children: React.ReactNode;
  orderId?: string;
  noVerticalPadding?: boolean;
  /**
   * External trigger to close the sheet programmatically
   */
  shouldClose?: boolean;
  /**
   * If true, sheet will slide from top instead of bottom on iOS
   */
  fromTheTop?: boolean;
}

export const BottomSheet = ({
  closable,
  onClose,
  children,
  orderId,
  noVerticalPadding,
  shouldClose = false,
  fromTheTop = false,
}: Props) => {
  const browserUserId = useBrowserUserId();
  const [visible, setVisible] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);
  const { queryParams } = useValidateQueryParams();
  const { brandId, tableNumber, counterNumber } = queryParams();
  const { isRTL } = useLanguage();

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Detect if device supports touch events (for drag-to-dismiss)
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isDraggable = isTouchDevice && closable;

  // Use fromTheTop on iOS, otherwise use normal bottom behavior
  const shouldSlideFromTop = fromTheTop && isIOS;

  const buttonId = 'split-bill-bottom-sheet-close';
  const buttonName = 'Close split bill bottom sheet';
  const buttonSource = BUTTON_SOURCES.SPLIT_BILL_SHEET;

  useScrollLock(visible);

  useEffect(() => {
    // Trigger the slide up animation on mount.
    setVisible(true);
  }, []);

  useEffect(() => {
    if (shouldClose && visible) {
      isClosingRef.current = true;
      setVisible(false);
    }
  }, [shouldClose, visible]);

  // Listen for transition end to call onClose callback
  useEffect(() => {
    const sheetElement = sheetRef.current;
    if (!sheetElement) return;

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === sheetElement && isClosingRef.current && onClose) {
        isClosingRef.current = false;
        onClose();
      }
    };

    sheetElement.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      sheetElement.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [onClose]);

  const handleClose = useCallback(() => {
    trackButtonClick({
      buttonId,
      buttonName,
      buttonSource,
      urlParams: {
        brandId: brandId || undefined,
        orderId: orderId || undefined,
        tableNumber,
        counterNumber,
      },
      extraParams: {
        browser_user_id: browserUserId,
      },
    });
    isClosingRef.current = true;
    setVisible(false);
  }, [brandId, tableNumber, counterNumber, orderId, browserUserId]);

  const { dragHandlers, dragStyles, isDragging } = useDragToDismiss({
    onDismiss: handleClose,
    threshold: 100,
    enabled: isDraggable,
    direction: shouldSlideFromTop ? 'up' : 'down',
  });

  return (
    <div className={`${styles.container} ${shouldSlideFromTop ? styles.containerTop : ''}`}>
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${shouldSlideFromTop ? styles.sheetTop : ''} ${isDraggable ? '' : styles.notDraggable} ${visible ? (shouldSlideFromTop ? styles.visibleTop : styles.visible) : shouldSlideFromTop ? styles.hiddenTop : styles.hidden} ${noVerticalPadding && styles.noVerticalPadding} ${isDragging ? styles.dragging : ''}`}
        style={dragStyles}
        {...dragHandlers}
      >
        {closable && (
          <button
            onClick={handleClose}
            className={`${styles.closeButton} ${isRTL ? styles.rtl : ''} `}
            aria-label="Close"
            data-testid="close-sheet"
          >
            <CloseDelete color="currentColor" />
          </button>
        )}
        {children}
      </div>
    </div>
  );
};
